import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import type { SEOHandle } from '@nasa-gcn/remix-seo'
import { Form, data, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import {
	AuthAlt,
	AuthConsent,
	AuthErrors,
	AuthField,
	AuthForgotLink,
	AuthHeading,
	AuthShell,
	AuthSubmit,
	SocialButtons,
} from '#app/components/site/auth'
import { getLandlordSelfSignupMode } from '#app/services/module-access.server'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { login, requireAnonymous } from '#app/utils/auth.server'
import { requireNotSpam } from '#app/utils/honeypot.server'
import { useIsPending } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'
import { siteLinks } from '#app/utils/site/site-links'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import type { Route } from './+types/landlord.login'
import { handleNewSession } from './login.server'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const links = siteLinks()

const LoginFormSchema = z.object({
	username: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('landlord')
	// TT-197: the "Sign up" link is hidden while public sign-up is paused.
	const selfSignupOpen = (await getLandlordSelfSignupMode()) === 'all'
	return { authConfig, selfSignupOpen }
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await requireNotSpam(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				try {
					const session = await login({
						...data,
						expectedUserType: 'landlord',
					})
					if (!session) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Invalid email or password',
						})
						return z.NEVER
					}

					return { ...data, session }
				} catch (error) {
					// Handle userType mismatch error
					if (error instanceof Error && error.message.includes('user type')) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message:
								'This account is registered as a tenant. Please use the tenant login page.',
						})
						return z.NEVER
					}
					throw error
				}
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LandlordLoginPage({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const authConfig = loaderData.authConfig

	// Build signup link preserving redirectTo param
	const signupLink = redirectTo
		? `${authConfig.signupPath}?redirectTo=${encodeURIComponent(redirectTo)}`
		: authConfig.signupPath

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const copy = siteContent.auth

	return (
		<AuthShell audience="landlord">
			<Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />
				<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
				<AuthHeading eyebrow={copy.login.eyebrow} title={copy.role.landlord} />
				<AuthField
					label={copy.fields.email}
					hint={siteContent.auth.hints.email}
					type="email"
					autoComplete="email"
					inputProps={getInputProps(fields.username, { type: 'email' })}
					errors={fields.username.errors}
				/>
				<AuthField
					label={copy.fields.password}
					hint={siteContent.auth.hints.password}
					type="password"
					autoComplete="current-password"
					inputProps={getInputProps(fields.password, { type: 'password' })}
					errors={fields.password.errors}
				/>
				<AuthForgotLink to={authConfig.forgotPasswordPath} />
				<AuthErrors errors={form.errors} id={form.errorId} />
				<AuthSubmit pending={isPending}>{copy.login.submit}</AuthSubmit>
			</Form>
			<SocialButtons type="Login" redirectTo={redirectTo} userType="landlord" />
			{loaderData.selfSignupOpen ? (
				<AuthAlt
					text={copy.login.alt}
					link={{ label: copy.login.altLink, to: signupLink }}
				/>
			) : null}
			<AuthConsent kind="login" />
		</AuthShell>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Login to TrueTenant - Landlord' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
